import type {
  CarrierRuleContext,
  DismissOpportunityRequest,
  OpportunityDismissalMetadata,
  OpportunityMapItem as SharedOpportunityMapItem,
  OpportunityMapResponse as SharedOpportunityMapResponse,
  OpportunitySortField,
  OpportunityStatus,
  ProductType,
  ScoringReasonCode,
  UpdateOpportunityNotesRequest,
} from '@fig/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import apiClient, { type PaginatedResponse } from '../../lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface OpportunityListItem {
  id: string;
  propensityScore: number;
  estimatedValue: number | null;
  status: OpportunityStatus;
  dismissal: OpportunityDismissalMetadata;
  scoredAt: string;
  forwardedAt: string | null;
  forwardedBy: string | null;
  policyId: string;
  policyNumber: string | null;
  carrierName: string;
  productName: string | null;
  productType: ProductType;
  accountValue: number | null;
  surrenderLossPercentage: number | null;
  surrenderDate: string | null;
  policyAge: number | null;
  ownerAge: number | null;
  jurisdiction: string | null;
  clientId: string | null;
  clientName: string | null;
  clientAge: number | null;
  clientState: string | null;
  advisorId: string;
  agentName: string | null;
  agentStatus: string | null;
  salesTeam: string | null;
  taxQualification: string | null;
  incomeStatus: string;
  hasIncomeFeature: boolean | null;
  hasIncomeRider: boolean | null;
  incomeBenefitValue: number | null;
  /** Reviewer note. Null when unset — the API never returns an empty string. */
  notes: string | null;
}

export interface RelatedParty {
  id: string | null;
  name: string | null;
  relationshipType: string;
  partyType: 'person' | 'entity' | 'unknown';
  dateOfBirth: string | null;
  age: number | null;
  isPrimary: boolean;
}

export interface PolicyAllocationRate {
  rate: number | null;
  rateType: string | null;
}

export interface PolicyFundingAllocation {
  depositEndDate: string | null;
  depositRatesDetails: PolicyAllocationRate[] | null;
  fundAllocationName: string | null;
  fundPercentage: number | null;
  fundValue: number | null;
  indexDurationRatesDetails: PolicyAllocationRate[] | null;
}

export interface OpportunityDetail {
  id: string;
  propensityScore: number;
  estimatedValue: number | null;
  status: OpportunityStatus;
  dismissal: OpportunityDismissalMetadata;
  scoredAt: string;
  createdAt: string;
  updatedAt: string;
  scoreFactors: {
    factors: Array<{
      name: string;
      score: number;
      weight: number;
      status?: 'passed' | 'failed' | 'unknown';
      details?: string;
    }>;
    reasonCodes?: ScoringReasonCode[];
    carrierRuleContext?: CarrierRuleContext;
    decision?: {
      score: number;
      minimumScoreThreshold: number;
      meetsThreshold: boolean;
    };
    rulesConfigId?: string;
    rulesConfigVersion?: number;
    valueBreakdown?: Record<string, unknown>;
  };
  policy: {
    id: string;
    policyNumber: string | null;
    contractNumber: string | null;
    carrierName: string;
    productName: string | null;
    productType: ProductType;
    issueDate: string | null;
    surrenderDate: string | null;
    premium: number | null;
    accountValue: number | null;
    incomeStartDate: string | null;
    incomeStatus: string;
    hasIncomeFeature: boolean | null;
    hasIncomeRider: boolean | null;
    currentCapRate: number | null;
    currentParticipationRate: number | null;
    surrenderValue: number | null;
    surrenderChargePercentage: number | null;
    costBasis: number | null;
    policyAge: number | null;
    policyStatus: string | null;
    maturityDate: string | null;
    lineOfBusiness: string | null;
    taxQualification: string | null;
    spouseAge: number | null;
    guaranteedRate: number | null;
    initialInterestRate: number | null;
    incomeBenefitValue: number | null;
    fundingAllocations?: PolicyFundingAllocation[];
    jurisdiction: string | null;
    owner: RelatedParty | null;
    relatedParties: RelatedParty[];
    client: {
      id: string;
      name: string | null;
      dateOfBirth: string | null;
      age: number | null;
      state: string | null;
    } | null;
  };
  advisorId: string;
  agentName: string | null;
  agentStatus: string | null;
  notes: string | null;
  notesUpdatedAt: string | null;
  /** Email of whoever last edited the note; null when there is no note. */
  notesUpdatedBy: string | null;
}

export interface OpportunityStats {
  totalOpportunities: number;
  totalEstimatedValue: number;
  scoreDistribution: {
    high: number;
    medium: number;
    low: number;
  };
  byStatus: {
    new: number;
    actioned: number;
    dismissed: number;
    withdrawn: number;
  };
}

export interface OpportunityFilters {
  page?: number;
  pageSize?: number;
  salesTeamId?: string;
  advisorId?: string;
  carrierName?: string;
  productType?: ProductType;
  outOfSurrender?: boolean;
  minScore?: number;
  maxScore?: number;
  minClientAge?: number;
  maxClientAge?: number;
  minSurrenderLoss?: number;
  maxSurrenderLoss?: number;
  taxQualification?: string;
  status?: OpportunityStatus;
  clientName?: string;
  incomeStatus?: string;
  advisorStatus?: string;
  sortBy?: OpportunitySortField;
  sortOrder?: 'asc' | 'desc';
}

export interface OpportunityMapFilters {
  salesTeamId?: string;
  advisorId?: string;
  carrierName?: string;
  productType?: ProductType;
  outOfSurrender?: boolean;
  minScore?: number;
  maxScore?: number;
  minClientAge?: number;
  maxClientAge?: number;
  minSurrenderLoss?: number;
  maxSurrenderLoss?: number;
  taxQualification?: string;
  status?: OpportunityStatus;
  incomeStatus?: string;
  advisorStatus?: string;
  limit?: number;
}

export type OpportunityMapItem = SharedOpportunityMapItem;
export type OpportunityMapResponse = SharedOpportunityMapResponse;

// ============================================================================
// Query Keys
// ============================================================================

export const opportunityKeys = {
  all: ['opportunities'] as const,
  lists: () => [...opportunityKeys.all, 'list'] as const,
  list: (filters: OpportunityFilters) => [...opportunityKeys.lists(), filters] as const,
  map: (filters: OpportunityMapFilters) => [...opportunityKeys.all, 'map', filters] as const,
  details: () => [...opportunityKeys.all, 'detail'] as const,
  detail: (id: string) => [...opportunityKeys.details(), id] as const,
  annualStatement: (id: string) => [...opportunityKeys.details(), id, 'annual-statement'] as const,
  stats: (filters?: Partial<OpportunityFilters>) =>
    [...opportunityKeys.all, 'stats', filters] as const,
};

// ============================================================================
// API Functions
// ============================================================================

async function fetchOpportunities(
  filters: OpportunityFilters
): Promise<PaginatedResponse<OpportunityListItem>> {
  const params = new URLSearchParams();

  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.salesTeamId) params.set('salesTeamId', filters.salesTeamId);
  if (filters.advisorId) params.set('advisorId', filters.advisorId);
  if (filters.carrierName) params.set('carrierName', filters.carrierName);
  if (filters.productType) params.set('productType', filters.productType);
  if (filters.outOfSurrender !== undefined) {
    params.set('outOfSurrender', String(filters.outOfSurrender));
  }
  if (filters.minScore !== undefined) params.set('minScore', String(filters.minScore));
  if (filters.maxScore !== undefined) params.set('maxScore', String(filters.maxScore));
  if (filters.minClientAge !== undefined) params.set('minClientAge', String(filters.minClientAge));
  if (filters.maxClientAge !== undefined) params.set('maxClientAge', String(filters.maxClientAge));
  if (filters.minSurrenderLoss !== undefined)
    params.set('minSurrenderLoss', String(filters.minSurrenderLoss));
  if (filters.maxSurrenderLoss !== undefined)
    params.set('maxSurrenderLoss', String(filters.maxSurrenderLoss));
  if (filters.taxQualification) params.set('taxQualification', filters.taxQualification);
  if (filters.status) params.set('status', filters.status);
  if (filters.clientName) params.set('clientName', filters.clientName);
  if (filters.incomeStatus) params.set('incomeStatus', filters.incomeStatus);
  if (filters.advisorStatus) params.set('advisorStatus', filters.advisorStatus);
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

  const response = await apiClient.get<PaginatedResponse<OpportunityListItem>>(
    `/opportunities?${params.toString()}`
  );
  return response.data;
}

async function fetchOpportunityMap(
  filters: OpportunityMapFilters
): Promise<OpportunityMapResponse> {
  const params = new URLSearchParams();

  if (filters.salesTeamId) params.set('salesTeamId', filters.salesTeamId);
  if (filters.advisorId) params.set('advisorId', filters.advisorId);
  if (filters.carrierName) params.set('carrierName', filters.carrierName);
  if (filters.productType) params.set('productType', filters.productType);
  if (filters.outOfSurrender !== undefined) {
    params.set('outOfSurrender', String(filters.outOfSurrender));
  }
  if (filters.minScore !== undefined) params.set('minScore', String(filters.minScore));
  if (filters.maxScore !== undefined) params.set('maxScore', String(filters.maxScore));
  if (filters.minClientAge !== undefined) params.set('minClientAge', String(filters.minClientAge));
  if (filters.maxClientAge !== undefined) params.set('maxClientAge', String(filters.maxClientAge));
  if (filters.minSurrenderLoss !== undefined)
    params.set('minSurrenderLoss', String(filters.minSurrenderLoss));
  if (filters.maxSurrenderLoss !== undefined)
    params.set('maxSurrenderLoss', String(filters.maxSurrenderLoss));
  if (filters.taxQualification) params.set('taxQualification', filters.taxQualification);
  if (filters.status) params.set('status', filters.status);
  if (filters.incomeStatus) params.set('incomeStatus', filters.incomeStatus);
  if (filters.advisorStatus) params.set('advisorStatus', filters.advisorStatus);
  if (filters.limit) params.set('limit', String(filters.limit));

  const response = await apiClient.get<OpportunityMapResponse>(
    `/opportunities/map?${params.toString()}`
  );
  return response.data;
}

async function fetchOpportunityById(id: string): Promise<OpportunityDetail> {
  const response = await apiClient.get<OpportunityDetail>(`/opportunities/${id}`);
  return response.data;
}

interface AnnualStatementResponse {
  available: boolean;
}

async function fetchAnnualStatement(opportunityId: string): Promise<AnnualStatementResponse> {
  const response = await apiClient.get<AnnualStatementResponse>(
    `/opportunities/${opportunityId}/annual-statement`
  );
  return response.data;
}

export async function downloadAnnualStatement(opportunityId: string): Promise<void> {
  const response = await apiClient.get(
    `/opportunities/${opportunityId}/annual-statement/download`,
    { responseType: 'blob' }
  );

  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] ?? 'annual-statement.pdf';

  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function fetchOpportunityStats(
  filters?: Partial<OpportunityFilters>
): Promise<OpportunityStats> {
  const params = new URLSearchParams();

  if (filters?.advisorId) params.set('advisorId', filters.advisorId);
  if (filters?.carrierName) params.set('carrierName', filters.carrierName);
  if (filters?.productType) params.set('productType', filters.productType);
  if (filters?.outOfSurrender !== undefined) {
    params.set('outOfSurrender', String(filters.outOfSurrender));
  }

  const queryString = params.toString();
  const url = queryString ? `/opportunities/stats?${queryString}` : '/opportunities/stats';

  const response = await apiClient.get<OpportunityStats>(url);
  return response.data;
}

interface DismissOpportunityResponse {
  success: boolean;
  opportunityId: string;
  status: Extract<OpportunityStatus, 'dismissed'>;
  dismissedAt: string;
  dismissedUntil: string | null;
  dismissalDuration: OpportunityDismissalMetadata['dismissalDuration'];
  dismissalComment: string;
}

interface WithdrawOpportunityResponse {
  success: boolean;
  opportunityId: string;
  status: Extract<OpportunityStatus, 'withdrawn'>;
  forwardId: string;
  withdrawnAt: string;
}

interface UpdateOpportunityNotesResponse {
  success: boolean;
  opportunityId: string;
  notes: string | null;
  notesUpdatedAt: string | null;
  notesUpdatedBy: string | null;
}

async function dismissOpportunity(
  input: { id: string } & DismissOpportunityRequest
): Promise<DismissOpportunityResponse> {
  const response = await apiClient.post<DismissOpportunityResponse>(
    `/opportunities/${input.id}/dismiss`,
    {
      duration: input.duration,
      comment: input.comment,
    }
  );
  return response.data;
}

async function withdrawOpportunity(input: { id: string }): Promise<WithdrawOpportunityResponse> {
  const response = await apiClient.post<WithdrawOpportunityResponse>(
    `/opportunities/${input.id}/withdraw`
  );
  return response.data;
}

async function updateOpportunityNotes(
  input: { id: string } & UpdateOpportunityNotesRequest
): Promise<UpdateOpportunityNotesResponse> {
  const response = await apiClient.put<UpdateOpportunityNotesResponse>(
    `/opportunities/${input.id}/notes`,
    { notes: input.notes }
  );
  return response.data;
}

// ============================================================================
// Hooks
// ============================================================================

export function useOpportunities(
  filters: OpportunityFilters = {},
  options?: Omit<UseQueryOptions<PaginatedResponse<OpportunityListItem>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: opportunityKeys.list(filters),
    queryFn: () => fetchOpportunities(filters),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function useOpportunityMap(
  filters: OpportunityMapFilters = {},
  options?: Omit<UseQueryOptions<OpportunityMapResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: opportunityKeys.map(filters),
    queryFn: () => fetchOpportunityMap(filters),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useOpportunity(
  id: string,
  options?: Omit<UseQueryOptions<OpportunityDetail>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: opportunityKeys.detail(id),
    queryFn: () => fetchOpportunityById(id),
    enabled: !!id,
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useAnnualStatement(
  id: string,
  options?: Omit<UseQueryOptions<AnnualStatementResponse | null>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: opportunityKeys.annualStatement(id),
    queryFn: async () => {
      try {
        return await fetchAnnualStatement(id);
      } catch (error) {
        // 404 means no statement available — not an error
        if (error instanceof Error && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes — statements don't change often
    ...options,
  });
}

export function useOpportunityStats(
  filters?: Partial<OpportunityFilters>,
  options?: Omit<UseQueryOptions<OpportunityStats>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: opportunityKeys.stats(filters),
    queryFn: () => fetchOpportunityStats(filters),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useDismissOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dismissOpportunity,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<OpportunityDetail | undefined>(
        opportunityKeys.detail(variables.id),
        (current) =>
          current
            ? {
                ...current,
                status: data.status,
                dismissal: {
                  dismissedAt: data.dismissedAt,
                  dismissedUntil: data.dismissedUntil,
                  dismissalDuration: data.dismissalDuration,
                  dismissalComment: data.dismissalComment,
                },
              }
            : current
      );
      queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useWithdrawOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: withdrawOpportunity,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<OpportunityDetail | undefined>(
        opportunityKeys.detail(variables.id),
        (current) =>
          current
            ? {
                ...current,
                status: data.status,
                updatedAt: data.withdrawnAt,
              }
            : current
      );
      queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useUpdateOpportunityNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOpportunityNotes,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<OpportunityDetail | undefined>(
        opportunityKeys.detail(variables.id),
        (current) =>
          current
            ? {
                ...current,
                notes: data.notes,
                notesUpdatedAt: data.notesUpdatedAt,
                notesUpdatedBy: data.notesUpdatedBy,
              }
            : current
      );
      // The list view's note indicator and has_notes sort both read from the list
      // query, so it has to refetch for the icon to appear or disappear.
      queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}
